import {faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Upload, UploadProps} from 'antd';
import {RcFile} from 'antd/lib/upload';
import {UploadRequestOption} from 'rc-upload/lib/interface';
import {Dispatch, PropsWithChildren, SetStateAction, useCallback} from 'react';

import {checkIsFile} from '../../../page/main/help/technical-support/components/technical-support-create-form/technical-support-create-form-helper';
import {Locale} from '../../../provider/locale/locale';
import {useModal} from '../../../provider/modal/modal-hook';
import {CustomFileType, UploadFileResponseType} from '../../../service/file-upload/file-upload-type';
import {useFormRules} from '../../../service/form/form-rules-hook';

import * as styles from './file-form-input.scss';

type PropsType<FileType extends UploadFileResponseType> = {
    upload: (file: RcFile) => Promise<FileType>;
    setFileMap: Dispatch<SetStateAction<Record<string, CustomFileType & FileType>>>;
    maxFileSize?: number;
    validationRules?: Array<(file: RcFile) => Promise<void>>;
} & UploadProps<CustomFileType & FileType>;

export function FileUpload<FileType extends UploadFileResponseType>(
    props: PropsWithChildren<PropsType<FileType>>
): JSX.Element {
    const {
        upload,
        setFileMap,
        children,
        maxCount,
        maxFileSize = 100,
        fileList,
        validationRules = [],
        ...restProps
    } = props;

    const {validateFileSize} = useFormRules();
    const {modal} = useModal();

    const uploadCustomRequest = useCallback(
        async (options: UploadRequestOption) => {
            try {
                const file = options?.file;

                if (!checkIsFile(file)) {
                    return;
                }

                setFileMap((previous) => {
                    return {
                        ...previous,
                        [file.uid]: {
                            name: file?.name || '',
                            uid: file.uid,
                            url: URL.createObjectURL(file),
                            status: 'uploading',
                        } as CustomFileType & FileType,
                    };
                });

                const response = await upload(file);

                setFileMap((previous) => {
                    if (previous[file.uid]) {
                        Reflect.deleteProperty(previous, file.uid);
                        return {
                            ...previous,
                            [response.id]: {
                                ...response,
                                name: file?.name || '',
                                uid: response.id,
                                url: response.url,
                                status: 'done',
                            },
                        };
                    }

                    return previous;
                });
            } catch {
                const file = options?.file;

                if (!checkIsFile(file)) {
                    return;
                }

                setFileMap((previous) => {
                    if (previous[file.uid]) {
                        return {
                            ...previous,
                            [file.uid]: {
                                name: file?.name || '',
                                uid: file.uid,
                                url: URL.createObjectURL(file),
                                status: 'error',
                            } as CustomFileType & FileType,
                        };
                    }

                    return previous;
                });
            }
        },
        [upload, setFileMap]
    );

    // The limit has to account for the files already uploaded, not only for the current batch: antd trims
    // its own list by `maxCount`, but still sends an upload request for every file that gets here.
    const beforeUpload = useCallback(
        async (file: RcFile, batchFileList: Array<RcFile>) => {
            const uploadedCount = fileList?.length || 0;
            const batchIndex = batchFileList.findIndex((batchFile) => batchFile.uid === file.uid);
            const fileIndex = uploadedCount + Math.max(batchIndex, 0);

            if (maxCount && fileIndex >= maxCount) {
                // Report the limit once per batch — for the first file of the batch that does not fit.
                if (fileIndex === uploadedCount || fileIndex - 1 < maxCount) {
                    modal.error({
                        content: <Locale stringKey="VALIDATION__ERROR__FILE_UPLOAD__MAX_COUNT" valueMap={{maxCount}} />,
                        title: <Locale stringKey="VALIDATION__ERROR__FILE_UPLOAD__ERROR_TITLE" />,
                    });
                }

                return false;
            }

            await validateFileSize(file, maxFileSize);
            await Promise.all(validationRules.map(async (rule) => rule(file)));
            return true;
        },
        [fileList, maxCount, maxFileSize, modal, validateFileSize, validationRules]
    );

    return (
        <Upload<CustomFileType & FileType>
            beforeUpload={beforeUpload}
            customRequest={uploadCustomRequest}
            fileList={fileList}
            listType="picture"
            maxCount={maxCount}
            multiple
            showUploadList={false}
            {...restProps} // eslint-disable-line react/jsx-props-no-spreading
        >
            {maxCount && (fileList?.length || 0) >= maxCount
                ? null
                : children || (
                      <Button
                          className={styles.TechnicalSupportFileUploadButton}
                          icon={<FontAwesomeIcon icon={faUpload} />}
                      >
                          <Locale stringKey="BUTTON__UPLOAD" />
                      </Button>
                  )}
        </Upload>
    );
}
